import Phaser from 'phaser';
import { setupCamera } from '../dpr.js';
import type { TomeId } from '@roe/shared';
import { TOME_DEFINITIONS, TOME_IDS, getTomeLevel, getTomeBonus, MAX_TOME_TYPES } from '@roe/shared';
import { GameStateManager } from '../state/GameStateManager';

export class TomesScene extends Phaser.Scene {
  private gsm!: GameStateManager;
  private battleScene!: Phaser.Scene & { resumeBattle(): void };
  private scrollContainer!: Phaser.GameObjects.Container;
  private scrollY = 0;
  private maxScroll = 0;
  private scrollBarThumb: Phaser.GameObjects.Rectangle | null = null;
  private isDragging = false;

  constructor() {
    super({ key: 'TomesScene' });
  }

  init(data: { gsm: GameStateManager; battleScene: any }): void {
    this.gsm = data.gsm;
    this.battleScene = data.battleScene;
  }

  create(): void {
    setupCamera(this, this.cameras.main, 'overlay');
    this.cameras.main.setBackgroundColor('rgba(5, 3, 5, 0.97)');

    const W = 480;
    const HEADER_H = 70;
    const BOTTOM_PAD = 16;
    const CARD_H = 78;
    const CONTENT_H = TOME_IDS.length * CARD_H + 8 + BOTTOM_PAD;
    const SCROLL_AREA_H = 576 - HEADER_H - BOTTOM_PAD;
    this.maxScroll = Math.max(0, CONTENT_H - SCROLL_AREA_H);
    this.scrollY = 0;
    this.isDragging = false;

    // Bottom border strip so cards don't bleed to edge
    this.add.rectangle(W / 2, 576 - BOTTOM_PAD / 2, W, BOTTOM_PAD, 0x0a0806, 1).setDepth(10);
    this.add.rectangle(W / 2, 576 - BOTTOM_PAD, W, 1, 0x5a3600, 0.9).setDepth(10);

    // Scrollable container for tome cards
    this.scrollContainer = this.add.container(0, HEADER_H);

    let y = 4;
    for (const id of TOME_IDS) {
      this.drawTomeCard(id, 12, y, W - 24);
      y += CARD_H;
    }

    // Clip mask
    const maskShape = this.make.graphics({});
    maskShape.fillRect(0, HEADER_H, W, SCROLL_AREA_H);
    const mask = maskShape.createGeometryMask();
    this.scrollContainer.setMask(mask);

    // ── Header (fixed) ──
    this.add.rectangle(W / 2, 22, W, 44, 0x0a0806, 1).setOrigin(0.5, 0).setDepth(10);
    this.add.rectangle(W / 2, 44, W, 1, 0x5a3600, 0.9).setDepth(10);
    this.add.rectangle(W / 2, 0, W, 70, 0x0a0806, 1).setOrigin(0.5, 0).setDepth(9);

    this.add.text(W / 2, 14, '✦  Books  ✦', {
      fontSize: '16px', fontFamily: 'Georgia, serif', color: '#c8973a', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(11);

    // Close button — ESC key mirrors it.
    const doClose = () => {
      this.battleScene.resumeBattle();
      this.scene.stop();
    };
    const closeBtn = this.add.text(W - 20, 14, '✕', {
      fontSize: '16px', fontFamily: 'Georgia, serif', color: '#5a3600',
    }).setOrigin(0.5).setDepth(11).setInteractive({ useHandCursor: true });
    closeBtn.on('pointerover', () => closeBtn.setColor('#c8973a'));
    closeBtn.on('pointerout', () => closeBtn.setColor('#5a3600'));
    closeBtn.on('pointerdown', doClose);
    this.input.keyboard?.once('keydown-ESC', doClose);

    // Build limit info
    const chosen = this.gsm.player.chosenTomeTypes.length;
    const limitText = chosen >= MAX_TOME_TYPES
      ? `Build locked: ${chosen}/${MAX_TOME_TYPES} types chosen`
      : `${chosen}/${MAX_TOME_TYPES} types chosen — ${MAX_TOME_TYPES - chosen} slot(s) left`;
    this.add.text(W / 2, 54, limitText, {
      fontSize: '9px', fontFamily: 'Georgia, serif',
      color: chosen >= MAX_TOME_TYPES ? '#8a4020' : '#6b4020',
      align: 'center', wordWrap: { width: W - 40 },
    }).setOrigin(0.5).setDepth(11);

    // Scroll bar
    if (this.maxScroll > 0) {
      const trackX = W - 6;
      this.add.rectangle(trackX, HEADER_H + SCROLL_AREA_H / 2, 4, SCROLL_AREA_H, 0x3a2800, 0.5).setDepth(12);
      const thumbH = Math.max(20, (SCROLL_AREA_H / CONTENT_H) * SCROLL_AREA_H);
      this.scrollBarThumb = this.add.rectangle(trackX, HEADER_H, 4, thumbH, 0x5a3600, 0.8)
        .setOrigin(0.5, 0).setDepth(12);
    }

    this.input.on('wheel', (_pointer: Phaser.Input.Pointer, _over: Phaser.GameObjects.GameObject[], _dx: number, deltaY: number) => {
      this.doScroll(deltaY * 0.5);
    });

    let dragStartY = 0;
    let dragScrollStart = 0;
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (pointer.y < HEADER_H) return;
      dragStartY = pointer.y;
      dragScrollStart = this.scrollY;
      this.isDragging = false;
    });
    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (!pointer.isDown || pointer.y < HEADER_H && !this.isDragging) return;
      const dy = dragStartY - pointer.y;
      if (Math.abs(dy) > 4) this.isDragging = true;
      if (this.isDragging) {
        this.scrollY = Phaser.Math.Clamp(dragScrollStart + dy, 0, this.maxScroll);
        this.applyScroll();
      }
    });
  }

  private doScroll(delta: number): void {
    this.scrollY = Phaser.Math.Clamp(this.scrollY + delta, 0, this.maxScroll);
    this.applyScroll();
  }

  private applyScroll(): void {
    const HEADER_H = 70;
    const BOTTOM_PAD = 16;
    const SCROLL_AREA_H = 576 - HEADER_H - BOTTOM_PAD;
    this.scrollContainer.y = HEADER_H - this.scrollY;

    if (this.scrollBarThumb && this.maxScroll > 0) {
      const pct = this.scrollY / this.maxScroll;
      const trackRange = SCROLL_AREA_H - this.scrollBarThumb.height;
      this.scrollBarThumb.y = HEADER_H + pct * trackRange;
    }
  }

  private drawTomeCard(id: TomeId, x: number, y: number, w: number): void {
    const def = TOME_DEFINITIONS[id];
    const level = getTomeLevel(this.gsm.player.tomes, id);
    const hasLevels = level > 0;
    const isChosen = this.gsm.player.chosenTomeTypes.includes(id);

    // Card background
    const tomeColor = Phaser.Display.Color.HexStringToColor(def.color).color;
    const bg = this.add.rectangle(x + w / 2, y + 30, w, 66,
      hasLevels ? 0x100a08 : 0x0d0a08, hasLevels ? 0.95 : 0.7)
      .setStrokeStyle(1, hasLevels ? tomeColor : 0x5a3600, hasLevels ? 0.8 : 0.5);
    this.scrollContainer.add(bg);

    // Icon — unified gold color for all tomes (coherence), dims when unowned
    const ICON_COL = 48;
    const icon = this.add.text(x + ICON_COL / 2, y + 30, def.icon, {
      fontSize: '24px', padding: { x: 4, y: 4 },
      fontFamily: "'Cinzel Decorative', 'Palatino Linotype', Georgia, serif", fontStyle: 'bold',
      color: hasLevels ? '#d4a84a' : '#5a3600',
      stroke: '#1a0f00', strokeThickness: 2,
    }).setOrigin(0.5, 0.5);
    this.scrollContainer.add(icon);

    // Name — starts after the icon column
    const name = this.add.text(x + ICON_COL + 6, y + 10, def.name, {
      fontSize: '11px', fontFamily: 'Georgia, serif',
      color: hasLevels ? def.color : '#5a3600', fontStyle: 'bold',
    });
    this.scrollContainer.add(name);

    // Vertical separator between icon column and text column
    const sep = this.add.rectangle(x + ICON_COL, y + 4, 1, 56, 0x2a1808, 0.5);
    this.scrollContainer.add(sep);

    if (hasLevels) {
      const lvl = this.add.text(x + w - 8, y + 8, `Level ${level}`, {
        fontSize: '10px', fontFamily: 'Georgia, serif', color: '#a07840',
      }).setOrigin(1, 0);
      this.scrollContainer.add(lvl);

      const bonus = getTomeBonus(this.gsm.player.tomes, id);
      const rawPct = bonus * 100;
      const fmtPct = Number.isInteger(rawPct) ? String(rawPct) : rawPct.toFixed(1);
      const bonusStr = def.bonusUnit.startsWith('%')
        ? `+${fmtPct}${def.bonusUnit}`
        : `+${Math.round(bonus * 100) / 100}${def.bonusUnit}`;
      const bonusText = this.add.text(x + ICON_COL + 6, y + 24, `Current: ${bonusStr}`, {
        fontSize: '9px', fontFamily: 'Georgia, serif', color: def.color,
      });
      this.scrollContainer.add(bonusText);

      // Progress bar
      const barStart = x + ICON_COL + 6;
      const barW = w - ICON_COL - 14 - 46; // leave room for the level label on the right
      const pct = Math.min(1, level / def.maxLevel);
      const barBg = this.add.rectangle(barStart + barW / 2, y + 44, barW, 5, 0x2a1a08);
      this.scrollContainer.add(barBg);
      if (pct > 0) {
        const barFill = this.add.rectangle(barStart + (barW * pct) / 2, y + 44, barW * pct, 5, tomeColor);
        this.scrollContainer.add(barFill);
      }
      const barLabel = this.add.text(barStart + barW + 8, y + 40, `${level}/${def.maxLevel}`, {
        fontSize: '9px', fontFamily: 'Georgia, serif', color: '#6b4020',
      });
      this.scrollContainer.add(barLabel);
    } else {
      const desc = this.add.text(x + ICON_COL + 6, y + 24, def.description, {
        fontSize: '9px', fontFamily: 'Georgia, serif', color: '#5a3600',
      });
      this.scrollContainer.add(desc);

      const status = isChosen
        ? 'In build — level up from boss & elite drops'
        : 'Not yet chosen — will appear in book choices';
      const statusText = this.add.text(x + ICON_COL + 6, y + 38, status, {
        fontSize: '9px', fontFamily: 'Georgia, serif', color: isChosen ? '#6b4020' : '#3a2800',
      });
      this.scrollContainer.add(statusText);
    }
  }
}
